import { useEffect, useState } from "react";
import { ShoppingCart, Mail, Loader2, Tag, RefreshCw, X } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

const eur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

function RelanceModal({ cart, onClose, onSent }) {
  const [discount, setDiscount] = useState(10);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/abandoned-carts/${cart.id}/relance`, { discount });
      toast.success(`Email envoyé à ${cart.email} — code ${data.coupon}`);
      onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="relance-modal">
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-semibold text-forest">Relancer {cart.name || cart.email}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-sage/50"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-stone">Un email de relance sera envoyé à <strong className="text-forest">{cart.email}</strong> avec un code promo sur son panier abandonné ({eur(cart.total)}).</p>
          <div>
            <label className="text-sm text-forest font-medium">Remise offerte (%)</label>
            <div className="flex gap-2 mt-2">
              {[5, 10, 15, 20].map((d) => (
                <button key={d} onClick={() => setDiscount(d)} data-testid={`discount-${d}`} className={`flex-1 rounded-lg py-2 text-sm font-semibold border transition-colors ${discount === d ? "bg-brand text-cream border-brand" : "border-border text-stone hover:border-brand"}`}>-{d}%</button>
              ))}
            </div>
          </div>
          <button onClick={send} disabled={busy} data-testid="relance-send" className="w-full rounded-full bg-brand text-cream py-3 font-semibold hover:bg-forest disabled:opacity-60 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} Envoyer la relance -{discount}%
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AbandonedCarts() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [relance, setRelance] = useState(null);

  const load = () => { setLoading(true); api.get("/abandoned-carts").then((r) => setCarts(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const totalValue = carts.reduce((s, c) => s + (c.total || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-4xl text-forest">Paniers abandonnés</h1>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-stone hover:text-forest"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Actualiser</button>
      </div>
      <p className="text-stone mb-6">{carts.length} panier(s) · <span className="font-semibold text-forest">{eur(totalValue)}</span> de valeur potentielle — relancez avec une remise 🎁</p>

      <div className="rounded-xl bg-white border border-border overflow-hidden">
        {loading ? <p className="text-stone text-sm p-10 text-center"><Loader2 className="animate-spin mx-auto" /></p>
          : carts.length === 0 ? <p className="text-stone text-sm p-10 text-center">Aucun panier abandonné 🎉</p> : (
          <div className="divide-y divide-border">
            {carts.map((c) => (
              <div key={c.id} className="px-5 py-4 flex items-center gap-4" data-testid={`cart-${c.id}`}>
                <div className="h-10 w-10 rounded-full bg-terracotta/10 flex items-center justify-center shrink-0"><ShoppingCart size={18} className="text-terracotta" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-forest truncate">{c.name || "Client"} · <span className="text-stone font-normal text-sm">{c.email}</span></p>
                  <p className="text-xs text-stone flex items-center gap-2 flex-wrap mt-0.5">
                    {fmt(c.updated_at)} · {c.count} article(s)
                    <span className="inline-flex flex-wrap gap-1">
                      {c.items?.slice(0, 3).map((it, k) => <span key={k} className="rounded bg-[#F7F7F8] px-1.5 py-0.5">{it.name} ×{it.quantity || 1}</span>)}
                    </span>
                    {c.reminded && <span className="text-brand flex items-center gap-0.5"><Tag size={11} /> Relancé{c.coupon ? ` (${c.coupon})` : ""}</span>}
                  </p>
                </div>
                <span className="font-semibold text-forest shrink-0 hidden sm:inline">{eur(c.total)}</span>
                <button onClick={() => setRelance(c)} data-testid={`relance-${c.id}`} className="rounded-full bg-brand/10 text-brand px-4 py-2 text-sm font-semibold hover:bg-brand hover:text-cream flex items-center gap-1.5 shrink-0"><Mail size={14} /> Relancer</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {relance && <RelanceModal cart={relance} onClose={() => setRelance(null)} onSent={() => { setRelance(null); load(); }} />}
    </div>
  );
}
